import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Clock, MapPin, Phone, PlayCircle } from "lucide-react";
import { PageHeading, PageShell } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CardSkeletonGrid, EmptyState, ErrorState } from "@/components/states";
import { useI18n } from "@/lib/i18n";
import { branchesQuery } from "@/lib/queries";
import { formatHours, isOpenAt, loc } from "@/lib/format";
import type { Branch } from "@/lib/types";
import { cn } from "@/lib/utils";
import { googleMapsUrl } from "@/lib/maps";
import { ResponsiveImage } from "@/components/ResponsiveImage";

export const Route = createFileRoute("/branches")({
  component: Branches,
});

function toEmbed(url?: string): string | null {
  if (!url) return null;
  const id =
    url.match(/[?&]v=([\w-]{6,})/)?.[1] ??
    url.match(/youtu\.be\/([\w-]{6,})/)?.[1] ??
    url.match(/embed\/([\w-]{6,})/)?.[1];
  return id ? `https://www.youtube.com/embed/${id}` : null;
}

function Branches() {
  const { t, lang } = useI18n();
  const { data, error, isPending, isError, refetch } = useQuery(branchesQuery());
  const [term, setTerm] = useState("");
  const [openOnly, setOpenOnly] = useState(false);
  const [tour, setTour] = useState<Branch | null>(null);

  const results = useMemo(() => {
    const q = term.trim().toLowerCase();
    return (data ?? []).filter((b) => {
      if (openOnly && !isOpenAt(b.openingHours)) return false;
      if (!q) return true;
      return [b.name.ar, b.name.en, b.address.ar, b.address.en].join(" ").toLowerCase().includes(q);
    });
  }, [data, term, openOnly]);

  return (
    <PageShell>
      <PageHeading
        breadcrumbs={[{ label: t("nav.home"), to: "/" }, { label: t("branches.title") }]}
        title={t("branches.title")}
        description={t("branches.desc")}
      />

      <div className="mb-6 flex flex-wrap items-center gap-4">
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={t("branches.search")}
          aria-label={t("branches.search")}
          className="h-12 max-w-sm rounded-2xl"
        />
        <div className="flex items-center gap-2">
          <Switch id="open-now" checked={openOnly} onCheckedChange={setOpenOnly} />
          <Label htmlFor="open-now">{t("branches.openNow")}</Label>
        </div>
        <p className="ms-auto text-sm font-bold text-muted-foreground">
          {t("branches.count", { count: results.length })}
        </p>
      </div>

      {isPending ? (
        <CardSkeletonGrid />
      ) : isError ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : results.length === 0 ? (
        <EmptyState title={t("branches.noResults")} description={t("common.errorHint")} />
      ) : (
        <div className={cn(
          "grid gap-6",
          results.length === 1 ? "mx-auto max-w-md" : "sm:grid-cols-2 lg:grid-cols-3",
        )}>
          {results.map((b) => {
            const open = isOpenAt(b.openingHours);
            return (
              <article key={b.id} className="surface-card overflow-hidden transition-shadow hover:shadow-lift">
                <div className="relative h-44">
                  <ResponsiveImage source={b.responsiveImage ?? b.image} sizes="(max-width: 639px) calc(100vw - 2rem), (max-width: 1023px) 50vw, 33vw" alt={loc(b.name, lang)} loading="lazy" className="size-full object-cover" />
                  <span
                    className={cn(
                      "absolute top-3 start-3 rounded-full px-3 py-1 text-xs font-bold shadow-soft",
                      open ? "bg-success text-success-foreground" : "bg-destructive text-destructive-foreground",
                    )}
                  >
                    {open ? t("branches.openNow") : t("branches.closedNow")}
                  </span>
                </div>
                <div className="grid gap-2 p-5">
                  <h2 className="text-lg font-bold">{loc(b.name, lang)}</h2>
                  <p className="flex items-start gap-2 text-sm text-muted-foreground">
                    <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                    {loc(b.address, lang)}
                  </p>
                  <p className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="size-4" aria-hidden="true" />
                    {formatHours(b.openingHours, lang)}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button asChild size="sm">
                      <Link to="/branches/$branchId" params={{ branchId: b.id }}>
                        {lang === "ar" ? "تفاصيل الفرع" : "Branch details"}
                      </Link>
                    </Button>
                    <Button asChild size="sm" variant="outline">
                      <a href={`tel:${b.phones[0]}`}>
                        <Phone className="size-4" />
                        {t("branches.call")}
                      </a>
                    </Button>
                    {b.coordinates ? (
                      <Button asChild size="sm" variant="outline">
                        <a
                          href={googleMapsUrl(b.coordinates)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <MapPin className="size-4" />
                          {t("branches.maps")}
                        </a>
                      </Button>
                    ) : null}
                    {toEmbed(b.youtubeUrl) ? (
                      <Button size="sm" onClick={() => setTour(b)}>
                        <PlayCircle className="size-4" />
                        {t("branches.tour")}
                      </Button>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Dialog open={Boolean(tour)} onOpenChange={(v) => !v && setTour(null)}>
        <DialogContent className="max-w-3xl rounded-3xl">
          <DialogHeader>
            <DialogTitle>{tour ? loc(tour.name, lang) : ""}</DialogTitle>
          </DialogHeader>
          {tour ? (
            <div className="aspect-video w-full overflow-hidden rounded-2xl bg-aubergine">
              <iframe
                src={toEmbed(tour.youtubeUrl) ?? ""}
                title={loc(tour.name, lang)}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                allowFullScreen
                className="size-full"
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
